// MG AI School API client (ARCHITECTURE section 15).
// JSON in and out, cookies always included, errors thrown as ApiError.
import { t } from './i18n'

/** Numbered page (feed, members, leaderboards, search, discovery). */
export type Page<T> = { items: T[]; page: number; limit: number; total: number; pages: number }

/** Cursor page (chat messages, notifications, comments). */
export type CursorPage<T> = { items: T[]; nextCursor: string | null }

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields: Record<string, unknown>

  constructor(status: number, code: string, message: string, fields: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fields = fields
  }

  /** True when the request never reached the server (offline, DNS, CORS, aborted). */
  get isNetwork(): boolean {
    return this.status === 0
  }

  /** A field value as text (validation messages, redirectSlug, requiredLevel). */
  field(name: string): string | undefined {
    const v = this.fields[name]
    return v === undefined || v === null ? undefined : String(v)
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

const DEFAULT_MESSAGES: Record<number, string> = {
  0: 'You seem to be offline. Check your connection and try again.',
  400: 'Something about that request was not right.',
  401: 'Please log in to continue.',
  402: 'This needs an active membership.',
  403: 'You do not have access to this.',
  404: 'We could not find that.',
  409: 'That already exists.',
  413: 'That file is too large.',
  429: 'Too many tries. Please wait a moment and try again.',
}

function defaultMessage(status: number): string {
  return DEFAULT_MESSAGES[status] ?? (status >= 500 ? 'Something went wrong on our side. Please try again.' : 'Something went wrong.')
}

function defaultCode(status: number): string {
  if (status === 0) return 'network_error'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 429) return 'rate_limited'
  if (status >= 500) return 'server_error'
  return 'http_' + status
}

export const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? ''

/** Absolute or proxied URL for an API path ("/api/..."). */
export function apiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path
  return API_BASE.replace(/\/$/, '') + (path.startsWith('/') ? path : '/' + path)
}

type Query = Record<string, string | number | boolean | null | undefined>

/** Appends a query string, skipping null, undefined and empty values. */
export function withQuery(path: string, query?: Query): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    params.set(k, String(v))
  }
  const qs = params.toString()
  if (!qs) return path
  return path + (path.includes('?') ? '&' : '?') + qs
}

export type RequestOptions = {
  query?: Query
  headers?: Record<string, string>
  signal?: AbortSignal
  /** Skip JSON encoding (FormData, Blob). */
  raw?: boolean
}

/** Static branded page for security blocks (ACCT-018, public/blocked.html). */
export const BLOCKED_PAGE = '/blocked.html'

let onBlocked: () => void = () => {
  if (typeof window === 'undefined' || window.location.pathname === BLOCKED_PAGE) return
  window.location.assign(BLOCKED_PAGE)
}

/** Replaces what happens when the edge (WAF, bot or VPN rules) blocks a request. Tests use this. */
export function setBlockedHandler(handler: () => void): void {
  onBlocked = handler
}

/**
 * A 403 without our JSON error body comes from the edge (a Cloudflare block page), not from the API:
 * show the branded blocked page instead of the generic "You do not have access" toast (Q-ACCT-13).
 */
function isEdgeBlock(res: Response): boolean {
  return res.status === 403 && !(res.headers.get('content-type') ?? '').includes('json')
}

async function parseError(res: Response): Promise<ApiError> {
  if (isEdgeBlock(res)) {
    onBlocked()
    return new ApiError(403, 'blocked', 'Our security checks blocked this connection. Try another network or turn off your VPN.')
  }
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  const err = (body && typeof body === 'object' && 'error' in body ? (body as { error: unknown }).error : null) as
    | { code?: unknown; message?: unknown; fields?: unknown }
    | null
  const code = typeof err?.code === 'string' && err.code ? err.code : defaultCode(res.status)
  const message = typeof err?.message === 'string' && err.message ? err.message : defaultMessage(res.status)
  const fields = err?.fields && typeof err.fields === 'object' ? (err.fields as Record<string, unknown>) : {}
  return new ApiError(res.status, code, message, fields)
}

const unauthorizedListeners = new Set<() => void>()

/** E2E-J2-02: every 401 answer (except the session probe itself) tells the auth layer to re check the session. */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

/** G-03 admin MFA: an admin route answered 403 mfa_required (verify again) or mfa_setup_required (no factor yet). */
export type MfaChallenge = { code: 'mfa_required' | 'mfa_setup_required'; stepUp: boolean; message: string }
type MfaHandler = (challenge: MfaChallenge) => Promise<boolean>
let mfaHandler: MfaHandler | null = null

/**
 * The MFA layer (features/auth/mfa/MfaProvider) registers here. It opens the verify dialog or the setup screen and
 * resolves true once this session passed the factor; the request then runs once more. Returns the unregister.
 */
export function setMfaHandler(handler: MfaHandler | null): () => void {
  mfaHandler = handler
  return () => {
    if (mfaHandler === handler) mfaHandler = null
  }
}

export function isMfaChallenge(error: unknown): error is ApiError {
  return isApiError(error) && error.status === 403 && (error.code === 'mfa_required' || error.code === 'mfa_setup_required')
}

/** Every API call: on a 403 MFA challenge the registered handler runs, and after a verify the call is retried once. */
export async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  try {
    return await requestOnce<T>(method, path, body, opts)
  } catch (e) {
    const handler = mfaHandler
    if (!isMfaChallenge(e) || !handler || opts.signal?.aborted) throw e
    const passed = await handler({ code: e.code as MfaChallenge['code'], stepUp: e.fields.stepUp === true, message: e.message })
    if (!passed || opts.signal?.aborted) throw e
    return requestOnce<T>(method, path, body, opts)
  }
}

async function requestOnce<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers }
  let payload: BodyInit | undefined
  if (body !== undefined && body !== null) {
    if (opts.raw || body instanceof FormData || body instanceof Blob) {
      payload = body as BodyInit
    } else {
      headers['Content-Type'] = 'application/json'
      payload = JSON.stringify(body)
    }
  }
  let res: Response
  try {
    res = await fetch(apiUrl(withQuery(path, opts.query)), {
      method,
      headers,
      body: payload,
      credentials: 'include',
      signal: opts.signal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new ApiError(0, 'network_error', defaultMessage(0))
  }
  if (!res.ok) {
    if (res.status === 401 && path.split('?')[0] !== '/api/auth/me') {
      unauthorizedListeners.forEach((listener) => {
        try {
          listener()
        } catch {
          /* a listener must never change the error the caller sees */
        }
      })
    }
    throw await parseError(res)
  }
  if (res.status === 204 || res.status === 205) return undefined as T
  const text = await res.text()
  if (!text) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(res.status, 'bad_response', 'The server sent a response we could not read.')
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts),
  del: <T = void>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('DELETE', path, body, opts),
}

/** Maps ApiError.fields onto react-hook-form (or any setError(name, {message})). Returns true when mapped. */
export function applyFieldErrors(
  error: unknown,
  setError: (name: never, err: { type: string; message: string }) => void,
): boolean {
  if (!isApiError(error)) return false
  let mapped = false
  for (const [name, message] of Object.entries(error.fields)) {
    if (name === 'redirectSlug' || name === 'requiredLevel') continue
    if (typeof message !== 'string') continue
    setError(name as never, { type: 'server', message })
    mapped = true
  }
  return mapped
}

/**
 * G-02 runtime kill switches: while a platform admin has paused a part of the school, the API answers 503 with
 * one of these codes. The member sees a friendly, translated line instead of the English server text.
 */
export function pausedMessage(code: string): string | null {
  switch (code) {
    case 'signups_paused':
      return t('New signups are paused for a little while. Please try again later.')
    case 'payments_paused':
      // platform-account-11: people who are joining read it too, so it speaks of existing memberships, not "yours"
      return t('Payments are paused for a little while. Please try again later. Existing memberships are not affected.')
    case 'uploads_paused':
      return t('Uploads are paused for a little while. Please try again later.')
    case 'ai_paused':
      return t('AI features are paused for a little while. Please try again later.')
    default:
      return null
  }
}

/** User facing message for any thrown value. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return pausedMessage(error.code) ?? error.message
  return defaultMessage(500)
}
